import { useCallback, useEffect, useState } from 'react'

/**
 * «تثبيت التطبيق» — the PWA install affordance.
 *
 * ── THE EVENT FIRES ONCE, AND BEFORE REACT IS LISTENING ─────────────────────
 *
 * Chromium dispatches `beforeinstallprompt` as soon as it decides the app is
 * installable, which is routinely BEFORE the first effect runs. A hook that only
 * calls `addEventListener` in `useEffect` therefore misses it on a cold load and
 * the button never appears — intermittently, and more often on a fast machine,
 * which is the worst possible failure to reproduce.
 *
 * So the event is captured by `startPwaInstallWatch()`, called from the app's
 * ENTRY bundle, and parked in `deferred` for whatever mounts later. The hook then
 * only has to handle the case where the event arrives after mount.
 *
 * ── IT IS NOT A STANDARD EVENT ──────────────────────────────────────────────
 *
 * `beforeinstallprompt` is Chromium-only and is not in the DOM lib, hence the
 * local interface. Firefox and Safari never fire it: on those the hook reports
 * `canInstall: false` for ever and the caller shows nothing, which is correct —
 * iOS installs through Share → Add to Home Screen and there is no API to trigger
 * it, so a button that cannot do anything would be a lie.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()
let started = false

const notify = () => {
  for (const l of listeners) l()
}

/**
 * Start listening. CALLED FROM `main.tsx`, not from this module's own top level.
 *
 * Registering at module scope here looked equivalent and was not: this module is
 * reached only through `ViewSettings`, which is inside the reader ROUTE CHUNK, so
 * it is fetched and executed well after the document has parsed. Measured with a
 * synthetic event fired at `DOMContentLoaded`: the button never appeared.
 *
 * `main.tsx` is the entry bundle and runs before `DOMContentLoaded`, so the
 * listener is in place for the earliest moment Chromium could fire. Idempotent,
 * because React's StrictMode double-invokes a great many things and a second
 * registration would double every notification.
 */
export function startPwaInstallWatch(): void {
  if (started || typeof window === 'undefined') return
  started = true

  window.addEventListener('beforeinstallprompt', (e) => {
    // Without this the browser shows its own mini-infobar and never hands us the
    // event to fire later from our own button.
    e.preventDefault()
    deferred = e as BeforeInstallPromptEvent
    notify()
  })

  // Chromium does not re-fire `beforeinstallprompt` after a successful install,
  // and the stale deferred event would keep the button on screen offering to
  // install something already installed.
  window.addEventListener('appinstalled', () => {
    deferred = null
    notify()
  })
}

/** True when the app is already running from the home screen / app window. */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari's own flag; it predates `display-mode` and is still the only
    // signal there.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export interface PwaInstall {
  /** Whether an install can actually be triggered right now. */
  canInstall: boolean
  /** Whether the app is already installed and running standalone. */
  installed: boolean
  /** Fires the browser's install dialog. Resolves once the reader has answered. */
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>
}

export function usePwaInstall(): PwaInstall {
  const [available, setAvailable] = useState(() => deferred !== null)
  const [installed, setInstalled] = useState(isStandalone)

  useEffect(() => {
    const onChange = () => {
      setAvailable(deferred !== null)
      setInstalled(isStandalone())
    }
    listeners.add(onChange)
    // The event may have landed between this module's import and this effect.
    onChange()
    return () => {
      listeners.delete(onChange)
    }
  }, [])

  const install = useCallback(async () => {
    const e = deferred
    if (!e) return 'unavailable' as const

    await e.prompt()
    const { outcome } = await e.userChoice

    // SINGLE USE: the deferred event cannot be prompted twice, so it is dropped
    // either way. Chromium fires a fresh one later if the reader declined and the
    // app is still installable; holding on to the spent one would give us a button
    // that silently does nothing on the second press.
    deferred = null
    notify()
    return outcome
  }, [])

  return { canInstall: available && !installed, installed, install }
}
